import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EffectRef,
  inject,
  Injector,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Optional,
  runInInjectionContext,
  Self,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl } from '@angular/forms';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DsButtonVariants, DsTooltipPosition, DsBannerState, Icons } from '@levelaccess/design-system';

import { TranslateService } from '../../../../translate/translate.service';
import { ITenantDpComplexityCounts } from '../../../../../../shared/interfaces/tenant.interface';
import { DEFAULT_DP_COMPLEXITY_COUNTS } from '../../../../../../shared/constants/tenant';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-digital-asset-complexity-calculator',
  templateUrl: './digital-asset-complexity-calculator.component.html',
  styleUrls: ['./digital-asset-complexity-calculator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule],
  standalone: true,
})
export class DigitalAssetComplexityCalculatorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  public digitalPropertyLimitControl: InputSignal<AbstractControl<number>> = input<AbstractControl<number>>();
  public formValidationRequest$: InputSignal<Observable<void>> = input<Observable<void>>();
  public controlName: InputSignal<string> = input<string>('digitalPropertyLimit');
  public allocatedDigitalProperties: InputSignal<number> = input<number>(0);

  public totalSignal: WritableSignal<number> = signal(null);
  public highSignal: WritableSignal<number> = signal(0);
  public mediumSignal: WritableSignal<number> = signal(0);
  public lowSignal: WritableSignal<number> = signal(0);

  public sumSignal: Signal<number> = computed(() => this.highSignal() + this.mediumSignal() + this.lowSignal());
  public differenceSignal: Signal<number> = computed(() => this.totalSignal() - this.sumSignal());

  // Read validation state from the form control's errors (set by the validator)
  private controlErrorsSignal: WritableSignal<Record<string, unknown> | null> = signal(null);

  public bannerState: Signal<DsBannerState | null> = computed(() => {
    if (this.allocatedDigitalProperties() > this.totalSignal()) {
      return DsBannerState.error;
    }

    const errors = this.controlErrorsSignal();
    if (!errors) {
      return null;
    }

    if (errors['nonNumericComplexityValue'] || errors['negativeComplexityValue'] || errors['fractionalComplexityValue']) {
      return DsBannerState.error;
    }

    if (errors['noDigitalAssets']) {
      return DsBannerState.info;
    }

    if (errors['complexityCountMismatch']) {
      const mismatch = errors['complexityCountMismatch'] as { difference: number };
      if (mismatch.difference > 0) {
        return DsBannerState.warning;
      }
      return DsBannerState.error;
    }

    return null;
  });

  public bannerMessage: Signal<string> = computed(() => {
    if (this.allocatedDigitalProperties() > this.totalSignal()) {
      return this.translateService.instant('package_limits_lower_than_usage');
    }

    const errors = this.controlErrorsSignal();
    if (!errors) {
      return '';
    }

    if (errors['nonNumericComplexityValue']) {
      return this.translateService.instant('validation_numeric_values_only');
    }
    if (errors['negativeComplexityValue']) {
      return this.translateService.instant('validation_no_negative_numbers');
    }
    if (errors['fractionalComplexityValue']) {
      return this.translateService.instant('validation_whole_numbers_only');
    }
    if (errors['noDigitalAssets']) {
      return this.translateService.instant('message_add_at_least_one_digital_asset');
    }
    if (errors['complexityCountMismatch']) {
      const mismatch = errors['complexityCountMismatch'] as { difference: number };
      const difference = mismatch.difference;
      if (difference > 0) {
        return this.translateService.instant('validation_assign_complexity_to_all_assets', [difference]);
      }
      const absDifference = Math.abs(difference);
      return this.translateService.instant('validation_too_many_assets_assigned_complexity', [absDifference]);
    }

    return '';
  });

  public subscription: Subscription = new Subscription();

  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly DsTooltipPosition: typeof DsTooltipPosition = DsTooltipPosition;
  public readonly Icons: typeof Icons = Icons;
  public readonly DsBannerState: typeof DsBannerState = DsBannerState;
  public readonly minInputValue: number = 0;
  public readonly maxInputValue: number = Number.MAX_SAFE_INTEGER;

  private syncEffect: EffectRef | null = null;
  private injector: Injector = inject(Injector);
  private preservePackageUsageLimits$: Observable<boolean>;
  private onChange: (value: ITenantDpComplexityCounts) => void = (): void => {};
  private onTouched = (): void => {};
  private currentComplexityCounts: ITenantDpComplexityCounts | null = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    @Optional() @Self() private ngControl: NgControl,
  ) {
    this.preservePackageUsageLimits$ = this.featureFlagService
      .variation$(FeatureFlagCollection.preservePackageUsageLimits, false)
      .pipe(map(Boolean));

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    const dpLimitControl = this.digitalPropertyLimitControl();

    this.subscription.add(
      this.preservePackageUsageLimits$.subscribe((preservePackageUsageLimits: boolean) => {
        const defaultTotal = preservePackageUsageLimits ? null : 0;
        const initialComplexityCounts: ITenantDpComplexityCounts = this.currentComplexityCounts ?? {
          ...DEFAULT_DP_COMPLEXITY_COUNTS,
        };
        this.totalSignal.set(dpLimitControl?.value ?? defaultTotal);
        this.highSignal.set(initialComplexityCounts.high ?? 0);
        this.mediumSignal.set(initialComplexityCounts.medium ?? 0);
        this.lowSignal.set(initialComplexityCounts.low ?? 0);

        if (SharedCommonUtility.isNullish(dpLimitControl?.value)) {
          this.updateDpLimitControlValue(this.totalSignal());
        }
      }),
    );

    if (dpLimitControl) {
      this.subscription.add(
        combineLatest([dpLimitControl.valueChanges, this.preservePackageUsageLimits$]).subscribe(
          ([value, preservePackageUsageLimits]: [number, boolean]) => {
            const defaultTotal = preservePackageUsageLimits ? null : 0;
            const numValue = Number(value) || defaultTotal;
            if (numValue !== this.totalSignal()) {
              this.totalSignal.set(numValue);
              this.changeDetectorRef.detectChanges();
            }
          },
        ),
      );
    }

    // Subscribe to the control's status changes to update errors signal
    if (this.ngControl?.control) {
      this.controlErrorsSignal.set(this.ngControl.control.errors);
      this.subscription.add(
        this.ngControl.control.statusChanges.subscribe(() => {
          this.controlErrorsSignal.set(this.ngControl.control?.errors ?? null);
          this.changeDetectorRef.markForCheck();
        }),
      );
    }

    runInInjectionContext(this.injector, () => {
      this.syncEffect = effect(() => {
        const highValue = this.highSignal();
        const mediumValue = this.mediumSignal();
        const lowValue = this.lowSignal();

        const currentCounts = this.currentComplexityCounts ?? { ...DEFAULT_DP_COMPLEXITY_COUNTS };
        const countsChanged =
          currentCounts.high !== highValue || currentCounts.medium !== mediumValue || currentCounts.low !== lowValue;
        if (countsChanged) {
          const newValue: ITenantDpComplexityCounts = { high: highValue, medium: mediumValue, low: lowValue };
          this.currentComplexityCounts = newValue;
          this.onChange(newValue);
          this.onTouched();
        }
      });
    });

    const formValidationReq = this.formValidationRequest$();
    if (formValidationReq) {
      this.subscription.add(
        formValidationReq.subscribe(() => {
          const dpControl = this.digitalPropertyLimitControl();
          if (dpControl) {
            dpControl.markAsDirty();
            dpControl.markAsTouched();
          }
          this.onTouched();
          this.changeDetectorRef.detectChanges();
        }),
      );
    }
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.syncEffect) {
      this.syncEffect.destroy();
    }
  }

  private updateDpLimitControlValue(value: number): void {
    const currentDpLimitControl = this.digitalPropertyLimitControl();
    if (currentDpLimitControl && currentDpLimitControl.value !== value) {
      currentDpLimitControl.setValue(value);
      currentDpLimitControl.markAsDirty();
      currentDpLimitControl.markAsTouched();
    }
  }

  private updateSignalFromInput(inputElement: HTMLInputElement, targetSignal: WritableSignal<number>): void {
    if (!this.validateInputValue(inputElement)) {
      inputElement.value = String(targetSignal());
      return;
    }
    targetSignal.set(Number(inputElement.value));
  }

  private validateInputValue(inputElement: HTMLInputElement): boolean {
    if (inputElement.validity.badInput) {
      return false;
    }

    const numValue: number = Number(inputElement.value);
    const isInRange: boolean = numValue >= this.minInputValue && numValue <= this.maxInputValue;
    if (!isInRange) {
      return false;
    }

    return true;
  }

  public onTotalChange(inputElement: HTMLInputElement): void {
    this.updateSignalFromInput(inputElement, this.totalSignal);
    this.updateDpLimitControlValue(this.totalSignal());
  }

  public onHighChange(inputElement: HTMLInputElement): void {
    this.updateSignalFromInput(inputElement, this.highSignal);
  }

  public onMediumChange(inputElement: HTMLInputElement): void {
    this.updateSignalFromInput(inputElement, this.mediumSignal);
  }

  public onLowChange(inputElement: HTMLInputElement): void {
    this.updateSignalFromInput(inputElement, this.lowSignal);
  }

  writeValue(value: ITenantDpComplexityCounts | null): void {
    this.currentComplexityCounts = value;
    if (value) {
      this.highSignal.set(value.high ?? 0);
      this.mediumSignal.set(value.medium ?? 0);
      this.lowSignal.set(value.low ?? 0);
    } else {
      this.highSignal.set(0);
      this.mediumSignal.set(0);
      this.lowSignal.set(0);
    }
    this.changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: (value: ITenantDpComplexityCounts) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
